import React, { useId } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TriggerConfig } from '../types';

interface TriggerNodeConfigProps {
  config: TriggerConfig;
  onConfigChange: (config: TriggerConfig) => void;
  onSave: () => void;
}

/** A labelled field inside the node config panel. */
const ConfigField = ({
  label,
  children,
}: {
  label: string;
  children: (fieldId: string) => React.ReactNode;
}) => {
  const fieldId = useId();
  return (
    <div>
      <Label htmlFor={fieldId} className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children(fieldId)}
    </div>
  );
};

const FREQUENCIES: Array<{ value: NonNullable<TriggerConfig['frequency']>; labelKey: string }> = [
  { value: 'hourly', labelKey: 'PAGES.API_INTEGRATION.FREQUENCIES.HOURLY' },
  { value: 'every_6h', labelKey: 'PAGES.API_INTEGRATION.FREQUENCIES.EVERY_6H' },
  { value: 'daily', labelKey: 'PAGES.API_INTEGRATION.FREQUENCIES.DAILY' },
  { value: 'weekly', labelKey: 'PAGES.API_INTEGRATION.FREQUENCIES.WEEKLY' },
];

export const TriggerNodeConfig: React.FC<TriggerNodeConfigProps> = ({
  config,
  onConfigChange,
  onSave,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      {/* Sync Mode */}
      <ConfigField label={t('PAGES.API_INTEGRATION.SYNC_MODE')}>
        {(fieldId) => (
          <Select
            value={config.syncMode}
            onValueChange={(value) => {
              const newMode = value as TriggerConfig['syncMode'];
              const updated: TriggerConfig = { ...config, syncMode: newMode };
              if (newMode !== 'scheduled') delete updated.frequency;
              onConfigChange(updated);
            }}
          >
            <SelectTrigger id={fieldId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">{t('PAGES.API_INTEGRATION.SYNC_MODES.MANUAL')}</SelectItem>
              <SelectItem value="scheduled">{t('PAGES.API_INTEGRATION.SYNC_MODES.SCHEDULED')}</SelectItem>
            </SelectContent>
          </Select>
        )}
      </ConfigField>

      {/* Frequency — only for scheduled */}
      {config.syncMode === 'scheduled' && (
        <div data-testid="frequency-selector">
          <ConfigField label={t('PAGES.API_INTEGRATION.FREQUENCY')}>
            {(fieldId) => (
              <Select
                value={config.frequency ?? ''}
                onValueChange={(value) =>
                  onConfigChange({ ...config, frequency: value as TriggerConfig['frequency'] })
                }
              >
                <SelectTrigger id={fieldId} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map(f => (
                    <SelectItem key={f.value} value={f.value}>{t(f.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </ConfigField>
        </div>
      )}

      {/* Save button — right-aligned, compact */}
      <div className="flex justify-end pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={onSave}
          disabled={config.syncMode === 'scheduled' && !config.frequency}
        >
          <Check className="size-3.5" />
          {t('PAGES.API_INTEGRATION.SAVE')}
        </Button>
      </div>
    </div>
  );
};
